import { MESApiService, getMomentTz, spParameter } from "@dexteel/mesf-core";

import { get, isBoolean } from "lodash-es";
import { CasterChemistry } from "../models/CasterChemistry";
import { CasterProduction } from "../models/CasterProduction";
import { CountersByShift } from "../models/CountersByShift";
import { HeatData, ProductBatch } from "../models/DeliveryTicket";
import { ElementData } from "../models/ElementData";
import {
	CasterProductionFilter,
	MeltShopProductionShiftFilter,
} from "../models/Filters";
import { ProbeData } from "../models/ProbeData";
import { ProcessByShift } from "../models/ProcessByShift";
import { ProcessData } from "../models/ProcessData";
import { ScrapData } from "../models/ScrapData";
import { StrandData } from "../models/StrandData";
import { StrandTimeSeriesData } from "../models/StrandSpeed";
import { LostStrandType } from "../models/caster.models";
import { AlertConfiguration } from "../models/catalogs/alertConfiguration";
import { HeatReconciliation } from "../models/catalogs/heatReconciliation";

const moment = getMomentTz();

export const exportCasterProductions = async (
	parameters: CasterProductionFilter,
) => {
	try {
		const sqlParameters = [];
		sqlParameters.push({
			name: "@StartDate",
			value: JSON.parse(JSON.stringify(parameters.startDate)),
		});
		sqlParameters.push({
			name: "@EndDate",
			value: JSON.parse(JSON.stringify(parameters.endDate)),
		});
		sqlParameters.push({
			name: "@BilletSizeId",
			value: parameters.billetSizeId === 0 ? null : parameters.billetSizeId,
		});
		sqlParameters.push({
			name: "@GradeId",
			value: parameters.gradeId === 0 ? null : parameters.gradeId,
		});
		sqlParameters.push({
			name: "@ShiftId",
			value: parameters.shiftId === 0 ? null : parameters.shiftId,
		});
		sqlParameters.push({ name: "@ToJSON", value: false });

		const apiService = new MESApiService();
		const structureSheet = {
			showAllSheets: true,
			sheets: [
				{
					order: 1,
					resultSetIndex: 0,
					sheetName: "Caster Production",
					showAllColumns: false,
					columns: [
						{
							columnName: "HeatName.Value",
							caption: "Heat",
							order: 1,
						},
						{
							columnName: "Grade.Value",
							caption: "Grade",
							order: 2,
						},
						{
							columnName: "BilletSizeName",
							caption: "Billet Size",
							order: 3,
						},
						{
							columnName: "Open.Free.Value",
							caption: "Open Free",
							order: 4,
						},
						{
							columnName: "Open.Burn.Value",
							caption: "Open Burn",
							order: 5,
						},
						{
							columnName: "NozzleSize",
							caption: "Nozzle",
							order: 6,
						},
						{
							columnName: "Trim.Temperature",
							caption: "Ladle Trim Temp",
							order: 7,
						},
						{
							columnName: "Trim.SuperHeat.Value",
							caption: "Ladle Trim SHeat",
							order: 8,
						},
						{
							columnName: "Tundish.Number",
							caption: "Tundish Num",
							order: 9,
						},
						{
							columnName: "Tundish.Sequence",
							caption: "Tundish Seq",
							order: 10,
						},
						{
							columnName: "Temperatures.Liquidus",
							caption: "Temperatures Liquidus",
							order: 11,
						},
						{
							columnName: "Temperatures.Minimum",
							caption: "Temperatures Minimum",
							order: 12,
						},
						{
							columnName: "Temperatures.Maximum",
							caption: "Temperatures Maximum",
							order: 13,
						},
						{
							columnName: "Temperatures.MinSH.Value",
							caption: "Temperatures MinSH",
							order: 14,
						},
						{
							columnName: "LadleOpen",
							caption: "Times Open",
							order: 15,
						},
						{
							columnName: "LadleClose",
							caption: "Times Close",
							order: 16,
						},
						{
							columnName: "Duration.Value",
							caption: "Times Duration",
							order: 17,
						},
						{
							columnName: "AvgSpeed.S1.Value",
							caption: "Avg Speed S1",
							order: 18,
						},
						{
							columnName: "AvgSpeed.S2.Value",
							caption: "Avg Speed S2",
							order: 19,
						},
						{
							columnName: "AvgSpeed.S3.Value",
							caption: "Avg Speed S3",
							order: 20,
						},
						{
							columnName: "Products.Pieces",
							caption: "Products Pieces",
							order: 21,
						},
						{
							columnName: "Products.Tons",
							caption: "Products Tons",
							order: 22,
						},
						{
							columnName: "Scrap.Pieces",
							caption: "Scrap Pieces",
							order: 23,
						},
						{
							columnName: "Scrap.Length",
							caption: "Scrap Length",
							order: 24,
						},
						{
							columnName: "Comments",
							caption: "Comments",
							order: 25,
						},
					],
				},
			],
		};
		const resp = await apiService.exportExcel(
			"[CAS].[GetProductionGridJSON]",
			sqlParameters,
			"Caster Production.xlsx",
			structureSheet,
		);
		return {
			ok: true,
			data: resp,
		};
	} catch (e: any) {
		return { ok: false, message: e.toString() };
	}
};
export const getCatalogs = async () => {
	try {
		const apiService = new MESApiService();
		const resp = await apiService.callJSON("[CAS].[GetCatalogsJSON]", []);
		if (resp.errorMessage !== null)
			return { ok: false, message: resp.errorMessage };
		return {
			ok: true,
			data: resp.result,
		};
	} catch (e: any) {
		return { ok: false, message: e.toString() };
	}
};
export const getShiftByParameters = async (
	parameters: MeltShopProductionShiftFilter,
) => {
	const utcString = parameters.productionDate?.toISOString();

	try {
		const sqlParameters = [];
		sqlParameters.push({
			name: "@ShiftId",
			value: parameters.shiftId === 0 ? null : parameters.shiftId,
		});
		sqlParameters.push({
			name: "@ProductionDate",
			value: utcString,
		});
		const apiService = new MESApiService();
		const resp = await apiService.callV2(
			"[MES].[GetShiftByParameters]",
			sqlParameters,
		);

		if (resp.ok) {
			return {
				ok: true,
				data: get(resp.data, "tables[0].rows[0]", {}),
			};
		} else {
			return {
				ok: false,
				message: resp.message,
			};
		}
	} catch (e: any) {
		return { ok: false, message: e.toString() };
	}
};
// Caster Dashboard
export const getCasterProductions = async (
	parameters: CasterProductionFilter,
) => {
	const sqlParameters = [];
	sqlParameters.push({
		name: "@StartDate",
		value: JSON.parse(JSON.stringify(parameters.startDate)),
	});
	sqlParameters.push({
		name: "@EndDate",
		value: JSON.parse(JSON.stringify(parameters.endDate)),
	});
	sqlParameters.push({
		name: "@BilletSizeId",
		value: parameters.billetSizeId === 0 ? null : parameters.billetSizeId,
	});
	sqlParameters.push({
		name: "@GradeId",
		value: parameters.gradeId === 0 ? null : parameters.gradeId,
	});
	sqlParameters.push({
		name: "@ShiftId",
		value: parameters.shiftId === 0 ? null : parameters.shiftId,
	});

	const apiService = new MESApiService();
	const resp = await apiService.callV2(
		"[CAS].[GetProductionGrid]",
		sqlParameters,
	);
	if (resp.ok) {
		resp.data.tables[0].rows = resp.data.tables[0].rows.map(
			(row: CasterProduction) => ({
				...row,
				LadleOpen: row.LadleOpen ? moment.utc(row.LadleOpen).toDate() : null,
				LadleClose: row.LadleClose ? moment.utc(row.LadleClose).toDate() : null,
			}),
		);
	}
	return resp;
};

export const getCasterChemistries = async (
	parameters: CasterProductionFilter,
) => {
	const sqlParameters = [];
	sqlParameters.push({
		name: "@StartDate",
		value: JSON.parse(JSON.stringify(parameters.startDate)),
	});
	sqlParameters.push({
		name: "@EndDate",
		value: JSON.parse(JSON.stringify(parameters.endDate)),
	});
	sqlParameters.push({
		name: "@BilletSizeId",
		value: parameters.billetSizeId === 0 ? null : parameters.billetSizeId,
	});
	sqlParameters.push({
		name: "@GradeId",
		value: parameters.gradeId === 0 ? null : parameters.gradeId,
	});
	sqlParameters.push({
		name: "@ShiftId",
		value: parameters.shiftId === 0 ? null : parameters.shiftId,
	});
	sqlParameters.push({
		name: "@IncludeExternalHeats",
		value: !isBoolean(parameters.includeExternalHeats)
			? null
			: parameters.includeExternalHeats,
	});

	const apiService = new MESApiService();
	const resp = await apiService.callV2(
		"[LAB].[GetMissedChemistriesGrid]",
		sqlParameters,
	);
	if (resp.ok) {
		resp.data.tables[0].rows = resp.data.tables[0].rows.map(
			(row: CasterChemistry) => ({
				...row,
				ResultsTime: row.ResultsTime
					? moment.utc(row.ResultsTime).toDate()
					: null,
			}),
		);
	}
	return resp;
};
//Caster Heat Report
export const getCasterHeatReport = async (processId: number) => {
	const sqlParameters = [];
	sqlParameters.push({ name: "@ProcessId", value: processId });

	const apiService = new MESApiService();
	return await apiService.callV2("[CAS].[GetHeatReport]", sqlParameters);
};
//
export const searchHeatName = async (searchText: string) => {
	try {
		const sqlParameters = [];
		sqlParameters.push({ name: "@SearchText", value: searchText.trim() });

		const apiService = new MESApiService();
		const resp = await apiService.callJSON(
			"[CAS].[SearchHeatNamesJSON]",
			sqlParameters,
		);
		if (resp.errorMessage !== null)
			return { ok: false, message: resp.errorMessage };
		return {
			ok: true,
			data: resp.result,
		};
	} catch (e: any) {
		return { ok: false, message: e.toString() };
	}
};

export const getChemistryHeatReport = async (heatId: number | null) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [];
	parameters.push({ name: "HeatId", value: heatId });

	const resp = await apiService.callV2("[LAB].[GetHeatReport]", parameters);

	if (resp.ok) {
		// table 0: heat info
		if (resp.data.tables[0] && resp.data.tables[0].rows[0]) {
			const heatInfo = resp.data.tables[0].rows[0];
			heatInfo.ProductionDate = heatInfo.ProductionDate
				? moment.utc(heatInfo.ProductionDate).toDate()
				: null;
		}

		if (resp.data.tables[1]) {
			resp.data.tables[1].rows = resp.data.tables[1].rows.map((row: any) => ({
				...row,
				Reception: row.Reception ? moment.utc(row.Reception).toDate() : null,
			}));
		}
	}
	return resp;
};

export const saveHeatReconciliation = async (heat: HeatReconciliation) => {
	const sqlParameters = [];
	sqlParameters.push({ name: "@ProcessId", value: heat.ProcessId });
	sqlParameters.push({ name: "@HeatName", value: heat.HeatName });
	sqlParameters.push({ name: "@ProductionYear", value: heat.ProductionYear });
	const apiService = new MESApiService();
	return await apiService.callV2("[CAS].[ChangeHeatToProcess]", sqlParameters);
};
export const getProcessesList = async (
	heatName: string,
	productionDate: Date | null,
) => {
	try {
		const sqlParameters = [];
		if (heatName.length > 0)
			sqlParameters.push({ name: "@HeatName", value: heatName });
		if (productionDate != null)
			sqlParameters.push({ name: "@ProductionDate", value: productionDate });

		const apiService = new MESApiService();
		const resp = await apiService.callJSON(
			"[CAS].[GetProcessesListJSON]",
			sqlParameters,
		);
		if (resp.errorMessage !== null)
			return { ok: false, message: resp.errorMessage };
		return {
			ok: true,
			data: resp.result,
		};
	} catch (e: any) {
		return { ok: false, message: e.toString() };
	}
};
export const getSectionsPerMoldSize = async (MoldSizeId: number | null) => {
	const apiService = new MESApiService();
	const resp = await apiService.callV2("[CAS].[GetSectionsPerMoldSize]", [
		{ name: "@MoldSizeId", value: MoldSizeId },
	]);
	return resp;
};

//Deprecated
export const GetAlertConfiguration = async () => {
	const apiService = new MESApiService();
	return await apiService.callV2("[CAS].[GetAlertConfiguration]", []);
};
export const saveAlertConfiguration = async (model: AlertConfiguration) => {
	const sqlParameters = [];
	sqlParameters.push({
		name: "@AlertConfigurationId",
		value: model.AlertConfigurationId,
	});
	sqlParameters.push({ name: "@Value", value: model.Value });
	sqlParameters.push({ name: "@CssClass", value: model.CssClass });
	const apiService = new MESApiService();
	return await apiService.callV2(
		"[CAS].[UpsertAlertConfiguration]",
		sqlParameters,
	);
};

//Caster Processes
export const getProcessesByShift = async (
	shiftId: number,
	deleted: boolean,
) => {
	const sqlParameters = [];
	sqlParameters.push({ name: "@ShiftId", value: shiftId });
	sqlParameters.push({ name: "@GetDeleted", value: deleted });
	const apiService = new MESApiService();
	const resp = await apiService.callV2(
		"[CAS].[GetProcessesByShift]",
		sqlParameters,
	);
	if (resp.ok) {
		resp.data.tables[0].rows = resp.data.tables[0].rows.map(
			(rows: ProcessByShift) => {
				return {
					...rows,
					LadleOpen: rows.LadleOpen
						? moment.utc(rows.LadleOpen).toDate()
						: null,
					LadleClose: rows.LadleClose
						? moment.utc(rows.LadleClose).toDate()
						: null,
				};
			},
		);
	}
	return resp;
};
export const upsertProcess = async (
	processId: number | null,
	ladleOpen: Date | null,
	ladleClose: Date | null,
) => {
	try {
		const apiService = new MESApiService();
		const parameters: spParameter[] = [];
		parameters.push({ name: "ProcessId", value: processId });
		parameters.push({
			name: "Start",
			value: ladleOpen === null ? null : ladleOpen.toISOString(),
		});
		parameters.push({
			name: "End",
			value: ladleClose === null ? null : ladleClose.toISOString(),
		});
		parameters.push({ name: "IsProcessManual", value: true });

		return await apiService.callV2("[CAS].[UpsertProcess]", parameters);
	} catch (e: any) {
		return { ok: false, message: e.toString() };
	}
};
export const deleteProcess = async (processId: number) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [];
	parameters.push({ name: "ProcessId", value: processId });
	try {
		return await apiService.callV2("[CAS].[DeleteProcess]", parameters);
	} catch (e: any) {
		return { ok: false, message: e.toString() };
	}
};
export const unDeleteProcess = async (processId: number) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [];
	parameters.push({ name: "ProcessId", value: processId });
	try {
		return await apiService.callV2("[CAS].[UnDeleteProcess]", parameters);
	} catch (e: any) {
		return { ok: false, message: e.toString() };
	}
};
export const unLinkProcess = async (processId: number) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [];
	parameters.push({ name: "ProcessId", value: processId });
	try {
		return await apiService.callV2("[CAS].[UnlinkProcessToHeat]", parameters);
	} catch (e: any) {
		return { ok: false, message: e.toString() };
	}
};
export const linkCasterProcess = async (processId: number, heatId: number) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [];
	parameters.push({ name: "ProcessId", value: processId });
	parameters.push({ name: "HeatId", value: heatId });
	parameters.push({ name: "IsHeatIdManual", value: true });
	try {
		return await apiService.callV2(
			"[CAS].[ManualLinkProcessToHeat]",
			parameters,
		);
	} catch (e: any) {
		return { ok: false, message: e.toString() };
	}
};

export const searchUnlinkedHeats = async (
	searchText: string,
	ladleOpen: Date,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({
		name: "SearchText",
		value: searchText,
	});

	parameters.push({
		name: "LadleOpen",
		value: ladleOpen,
	});
	const resp = await apiService.callV2(
		"[CAS].[SearchUnlinkedHeats]",
		parameters,
	);
	return resp;
};

//Caster Data Entry
export const getProcessDataById = async (processId: number | null) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [];
	parameters.push({ name: "ProcessId", value: processId });

	const resp = await apiService.callV2("[CAS].[DEGetProcess]", parameters);
	if (resp.ok) {
		resp.data.tables[0].rows = resp.data.tables[0].rows.map(
			(row: ProcessData) => ({
				...row,
				LadleOpen: row.LadleOpen ? moment.utc(row.LadleOpen).toDate() : null,
				LadleClose: row.LadleClose ? moment.utc(row.LadleClose).toDate() : null,
				TapTime: row.TapTime ? moment.utc(row.TapTime).toDate() : null,
				TrimProbeTime: row.TrimProbeTime
					? moment.utc(row.TrimProbeTime).toDate()
					: null,
			}),
		);
		resp.data.tables[1].rows = resp.data.tables[1].rows.map(
			(row: StrandData) => ({
				...row,
				StartTime: row.StartTime ? moment.utc(row.StartTime).toDate() : null,
				EndTime: row.EndTime ? moment.utc(row.EndTime).toDate() : null,
			}),
		);
		resp.data.tables[2].rows = resp.data.tables[2].rows.map(
			(row: ProbeData) => ({
				...row,
				Timestamp: row.Timestamp ? moment.utc(row.Timestamp).toDate() : null,
			}),
		);
	}
	return resp;
};

export const getLastestProcesses = async () => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [];

	return await apiService.callV2("[CAS].[GetLastestNProcesses]", parameters);
};
export const searchProcesses = async (searchTerm: string) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "SearchTerm", value: searchTerm },
		{ name: "MaxResults", value: 100 },
	];
	return await apiService.callV2("[CAS].[SearchProcesses]", parameters);
};
export const getChemistrySampleByHeatId = async (heatId: number | null) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [];
	parameters.push({ name: "HeatId", value: heatId });

	const resp = await apiService.callV2(
		"[LAB].[GetChemistrySampleByHeatId]",
		parameters,
	);
	if (resp.ok) {
		resp.data.tables[0].rows = resp.data.tables[0].rows.map(
			(row: ElementData) => ({
				...row,
				Reception: row.Reception ? moment.utc(row.Reception).toDate() : null,
			}),
		);
	}
	return resp;
};
export const getStrandSpeedsByProcessId = async (processId: number) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [];
	parameters.push({ name: "ProcessId", value: processId });

	const resp = await apiService.callV2(
		"[SWV].[GetStrandSpeedsByProcessId]",
		parameters,
	);
	if (resp.ok) {
		resp.data.tables[0].rows = resp.data.tables[0].rows.map(
			(row: StrandTimeSeriesData) => ({
				...row,
				Datetime: row.Datetime ? moment.utc(row.Datetime).toDate() : null,
			}),
		);
	}
	return resp;
};
export const getMoldLevelsByProcessId = async (processId: number) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [];
	parameters.push({ name: "ProcessId", value: processId });

	const resp = await apiService.callV2(
		"[SWV].[GetMoldLevelByProcessId]",
		parameters,
	);
	if (resp.ok) {
		resp.data.tables[0].rows = resp.data.tables[0].rows.map(
			(row: StrandTimeSeriesData) => ({
				...row,
				Datetime: row.Datetime ? moment.utc(row.Datetime).toDate() : null,
			}),
		);
	}
	return resp;
};
export const getBilletTempByProcessId = async (
	processId: number,
	offsetMinutes: number = 0,
) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [];
	parameters.push({ name: "ProcessId", value: processId });
	parameters.push({ name: "OffsetMinutes", value: offsetMinutes });

	const resp = await apiService.callV2(
		"[SWV].[GetBilletTempByProcessId]",
		parameters,
	);
	if (resp.ok) {
		resp.data.tables[0].rows = resp.data.tables[0].rows.map(
			(row: StrandTimeSeriesData) => ({
				...row,
				Datetime: row.Datetime ? moment.utc(row.Datetime).toDate() : null,
			}),
		);
	}
	return resp;
};
export const getGanttData = async (Start: Date | null, End: Date | null) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [];

	if (!moment.isDate(Start) || isNaN(Start.getTime())) {
		Start = null;
	}
	if (!moment.isDate(End) || isNaN(End.getTime())) {
		End = null;
	}

	parameters.push({ name: "Start", value: Start ? Start.toISOString() : null });
	parameters.push({ name: "End", value: End ? End.toISOString() : null });

	const resp = await apiService.callV2("[SWV].[GetGanttData]", parameters);
	return resp;
};

export const getDelayById = async (delayId: number) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [];
	parameters.push({ name: "DelayId", value: delayId });

	return await apiService.callV2("[DEL].[GetDelay]", parameters);
};

//Delivery Ticket
export const getDeliveryTicket = async (processId: number) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [];

	parameters.push({ name: "ProcessId", value: processId });

	const resp = await apiService.callV2("[SWV].[GetDeliveryTicket]", parameters);
	if (resp.ok) {
		resp.data.tables[0].rows = resp.data.tables[0].rows.map(
			(row: HeatData) => ({
				...row,
				CastDate: row.CastDate ? moment.utc(row.CastDate).toDate() : null,
				TapTime: row.TapTime ? moment.utc(row.TapTime).toDate() : null,
			}),
		);
		resp.data.tables[1].rows = resp.data.tables[1].rows.map(
			(row: ProductBatch) => ({
				...row,
				Timestamp: row.Timestamp ? moment.utc(row.Timestamp).toDate() : null,
			}),
		);
	}
	return resp;
};
export const getReadProductBatches = async (ProcessId: number) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [];

	parameters.push({ name: "ProcessId", value: ProcessId });

	const resp = await apiService.callV2(
		"[CAS].[ReadProductBatches]",
		parameters,
	);
	return resp;
};
export const getMoldSizes = async () => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [];

	const resp = await apiService.callV2("[CAS].[GetMoldSizes]", parameters);
	return resp;
};
export const getRealTimeData = async (shiftId: number) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [];
	parameters.push({ name: "@ShiftId", value: shiftId });
	const resp = await apiService.callV2("[SWV].[GetFacts]", parameters);
	return resp;
};

export const upsertProductBatch = async ({
	productBatchId,
	processId,
	length,
	sectionId,
	pieces,
	weight,
	isManualSaved,
	lowWeightConfirmed = false,
}: {
	productBatchId: number | null;
	processId: number;
	length: number;
	sectionId: number;
	pieces: number;
	weight: number | null;
	isManualSaved: boolean;
	lowWeightConfirmed?: boolean;
}) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [];
	if (productBatchId !== null && productBatchId < 0) {
		productBatchId = null;
	}
	if (!processId) {
		return {
			ok: false,
			message: "Process is required to product batch changes.",
		};
	}
	parameters.push(
		{ name: "ProductBatchId", value: productBatchId },
		{ name: "ProcessId", value: processId },
		{ name: "Length", value: length },
		{ name: "SectionId", value: sectionId },
		{ name: "Pieces", value: pieces },
		{ name: "Weight", value: weight },
		{ name: "isManualSaved", value: isManualSaved },
		{ name: "LowWeightConfirmed", value: lowWeightConfirmed },
	);
	const resp = await apiService.callV2(
		"[CAS].[UpsertProductBatch]",
		parameters,
	);
	return resp;
};
export const GetProcessCatalogs = async () => {
	const apiService = new MESApiService();
	return await apiService.callV2("[CAS].[DEGetCatalogs]", []);
};
export const upsertProcessData = async (
	processId: number,
	field: string,
	value: any,
) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [];
	parameters.push({ name: "ProcessId", value: processId });
	parameters.push({ name: field, value: value });
	parameters.push({ name: "IsProcessDataManual", value: true });

	try {
		return await apiService.callV2("[CAS].[UpdateProcessData]", parameters);
	} catch (e: any) {
		return { ok: false, message: e.toString() };
	}
};
export const upsertPourback = async (
	PourbackId: number | null,
	HeatId: number,
	SourceProcessId: number | null,
	PourbackWeight: number | null,
	PourbackReasonId: number | null,
	SourceTime: Date,
) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [];
	parameters.push({ name: "PourbackId", value: PourbackId });
	parameters.push({ name: "HeatId", value: HeatId });
	parameters.push({ name: "SourceProcessId", value: SourceProcessId });
	parameters.push({ name: "PourbackWeight", value: PourbackWeight });
	parameters.push({ name: "PourbackReasonId", value: PourbackReasonId });
	parameters.push({ name: "SourceTime", value: SourceTime.toISOString() });

	return await apiService.callV2("[MS].[UpsertPourback]", parameters);
};
export const deletePourback = async (PourbackId: number) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [];
	parameters.push({ name: "PourbackId", value: PourbackId });

	return await apiService.callV2("[MS].[DeletePourback]", parameters);
};
export const updateTrimData = async (
	processId: number,
	field: string,
	value: any,
) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [];
	parameters.push({ name: "ProcessId", value: processId });
	parameters.push({ name: field, value: value });
	parameters.push({ name: "IsTrimDataManual", value: true });

	try {
		return await apiService.callV2("[CAS].[UpdateTrimData]", parameters);
	} catch (e: any) {
		return { ok: false, message: e.toString() };
	}
};
export const upsertStrandData = async (
	processId: number,
	strand: number,
	field: string,
	value: any,
) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [];
	parameters.push({ name: "ProcessId", value: processId });
	parameters.push({ name: "Strand", value: strand });
	parameters.push({ name: field, value: value });
	parameters.push({ name: "IsStrandDataManual", value: true });

	return await apiService.callV2("[CAS].[UpdateStrandData]", parameters);
};

export const upsertAllStrandsData = async (
	processId: number,
	field: string,
	value: any,
) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [];
	parameters.push({ name: "ProcessId", value: processId });
	parameters.push({ name: field, value: value });
	parameters.push({ name: "IsStrandDataManual", value: true });

	return await apiService.callV2("[CAS].[UpdateStrandsData]", parameters);
};

export const updateLabTechnician = async (
	HeatId: number,
	Technician: string | null,
) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [];
	parameters.push({ name: "HeatId", value: HeatId });
	parameters.push({ name: "Technician", value: Technician });

	return await apiService.callV2("[LAB].[UpdateLabTechnician]", parameters);
};

export const upsertScrap = async (scrap: ScrapData) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [];
	parameters.push({ name: "ScrapId", value: scrap.ScrapId });
	parameters.push({ name: "ProcessId", value: scrap.ProcessId });
	parameters.push({ name: "Pieces", value: scrap.Pieces });
	parameters.push({ name: "Length", value: scrap.Length });
	parameters.push({ name: "ScrapReasonId", value: scrap.ScrapReasonId });
	parameters.push({ name: "Weight", value: scrap.Weight });
	parameters.push({ name: "Strand", value: scrap.Strand });
	return await apiService.callV2("[CAS].[UpsertScrapData]", parameters);
};

export const deleteScrap = async (scrapId: number) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [];
	parameters.push({ name: "ScrapId", value: scrapId });
	return await apiService.callV2("[CAS].[DeleteScrapData]", parameters);
};

export const upsertProbeData = async ({
	ProbeId,
	ProcessId,
	Temperature,
	Timestamp,
	Oxygen,
	IsProbeManual,
}: ProbeData) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [];
	parameters.push({ name: "ProbeId", value: ProbeId });
	parameters.push({ name: "ProcessId", value: ProcessId });
	parameters.push({ name: "Temperature", value: Temperature });
	parameters.push({ name: "Timestamp", value: Timestamp.toISOString() });
	if (Oxygen) parameters.push({ name: "Oxygen", value: Oxygen });
	if (IsProbeManual)
		parameters.push({ name: "IsProbeManual", value: IsProbeManual });

	return await apiService.callV2("[CAS].[UpsertProbe]", parameters);
};
export const deleteProbe = async (probeId: number) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [];
	parameters.push({ name: "ProbeId", value: probeId });
	try {
		return await apiService.callV2("[CAS].[DeleteProbe]", parameters);
	} catch (e: any) {
		return { ok: false, message: e.toString() };
	}
};
//Read PLC
export const ReadStrandDataFromPLC = async (
	processId: number,
	strand: number,
	forced: boolean,
) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [];
	parameters.push({ name: "ProcessId", value: processId });
	parameters.push({ name: "Strand", value: strand });
	parameters.push({ name: "Force", value: forced });
	try {
		return await apiService.callV2(
			"[SWV].[ReadCASStrandDataFromPLC]",
			parameters,
		);
	} catch (e: any) {
		return { ok: false, message: e.toString() };
	}
};
export const readProbesFromPLC = async (processId: number) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [];
	parameters.push({ name: "ProcessId", value: processId });

	return await apiService.callV2("[SWV].[ReadCASProbesFromPLC]", parameters);
};
export const CalculateProcessData = async (processId: number) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [];
	parameters.push({ name: "ProcessId", value: processId });
	try {
		return await apiService.callV2("[CAS].[CalculateProcessData]", parameters);
	} catch (e: any) {
		return { ok: false, message: e.toString() };
	}
};
//Caster Counters
export const getCountersByShift = async (shiftId: number, deleted: boolean) => {
	const sqlParameters = [];
	sqlParameters.push({ name: "@ShiftId", value: shiftId });
	sqlParameters.push({ name: "@GetDeleted", value: deleted });
	const apiService = new MESApiService();
	const resp = await apiService.callV2(
		"[CAS].[GetCountersByShift]",
		sqlParameters,
	);
	if (resp.ok) {
		resp.data.tables[0].rows = resp.data.tables[0].rows.map(
			(row: CountersByShift) => ({
				...row,
				LadleOpen: row.LadleOpen ? moment.utc(row.LadleOpen).toDate() : null,
			}),
		);
	}
	return resp;
};

export const ReadTrimDataFromPLC = async ({
	processId,
}: {
	processId: number;
}) => {
	const api = new MESApiService();
	return await api.callV2("[SWV].[ReadCASTrimDataFromPLC]", [
		{
			name: "ProcessId",
			value: processId,
		},
	]);
};

interface FailedStartupFilter {
	fromDate: Date;
	toDate: Date;
	shift?: string | null;
	crew?: string | null;
}

export const getFailedStartup = async ({
	fromDate,
	toDate,
	shift,
	crew,
}: FailedStartupFilter) => {
	const moment = getMomentTz();
	const api = new MESApiService();
	const parameters: spParameter[] = [];
	parameters.push({
		name: "@FromDate",
		value: fromDate.toISOString(),
	});
	parameters.push({
		name: "@ToDate",
		value: toDate.toISOString(),
	});
	parameters.push({
		name: "@Shift",
		value: shift || null,
	});
	parameters.push({
		name: "@Crew",
		value: crew || null,
	});

	const resp = await api.callV2("[CAS].[GetFailedStartup]", parameters);

	if (resp.ok) {
		resp.data.tables[0].rows = resp.data.tables[0].rows.map((row: any) => ({
			...row,
			LastEnd: moment.utc(row.LastEnd).toDate(),
			Start: moment.utc(row.Start).toDate(),
			End: moment.utc(row.End).toDate(),
			NextStart: moment.utc(row.NextStart).toDate(),
		}));
	}
	return resp;
};

export const getCatalogLostStrandTypes = async () => {
	const apiService = new MESApiService();
	const resp = await apiService.callV2("[CAS].[GetCatalogLostStrandTypes]", []);
	return resp;
};

export const upsertLostStrandType = async (data: LostStrandType) => {
	const apiService = new MESApiService();
	const resp = await apiService.callV2("[CAS].[UpsertLostStrandType]", [
		{ name: "LostStrandTypeId", value: data.LostStrandTypeId },
		{ name: "Description", value: data.Description },
		{ name: "IsActive", value: data.IsActive },
	]);
	return resp;
};

export const getLostStrandTypes = async () => {
	const apiService = new MESApiService();
	const resp = await apiService.callV2("[CAS].[GetLostStrandTypes]", []);
	return resp;
};

export const upsertLostStrand = async ({
	LostStrandId,
	ProcessId,
	StrandId,
	LostStrandTypeId,
	Comment,
	Timestamp,
	Author,
}: {
	LostStrandId: number;
	ProcessId: number;
	StrandId: number;
	LostStrandTypeId: number;
	Comment: string;
	Timestamp: Date;
	Author: string;
}) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [];

	parameters.push({ name: "LostStrandId", value: LostStrandId });
	parameters.push({ name: "ProcessId", value: ProcessId });
	parameters.push({ name: "StrandId", value: StrandId });
	parameters.push({ name: "LostStrandTypeId", value: LostStrandTypeId });
	parameters.push({ name: "Comment", value: Comment });
	parameters.push({
		name: "Timestamp",
		value: Timestamp ? Timestamp.toISOString() : null,
	});

	const resp = await apiService.callV2("[CAS].[UpsertLostStrand]", parameters);
	return resp;
};
export const deleteLostStrand = async (lostStrandId: number) => {
	const apiService = new MESApiService();
	const resp = await apiService.callV2("[CAS].[DeleteLostStrand]", [
		{ name: "LostStrandId", value: lostStrandId },
	]);
	return resp;
};
export const deleteLostStrandType = async (lostStrandTypeId: number) => {
	const apiService = new MESApiService();
	const resp = await apiService.callV2("[CAS].[DeleteLostStrandType]", [
		{ name: "LostStrandTypeId", value: lostStrandTypeId },
	]);
	return resp;
};

export const readLostStrandsFromPLC = async (processId: number) => {
	const apiService = new MESApiService();
	const resp = await apiService.callV2("[SWV].[TrackCASStrandEventsJob]", [
		{ name: "ProcessId", value: processId },
	]);
	return resp;
};

export const getProcessesWithoutDeliveryTicket = async (
	offsetTimeInMinutes: number,
) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "OffsetTimeInMinutes", value: offsetTimeInMinutes },
	];
	const resp = await apiService.callV2(
		"[CAS].[GetProcessesWithoutDeliveryTicket]",
		parameters,
	);
	if (resp.ok && resp.data.tables[0]) {
		resp.data.tables[0].rows = resp.data.tables[0].rows.map((row: any) => ({
			...row,
			LadleClose: row.LadleClose ? moment.utc(row.LadleClose).toDate() : null,
		}));
	}
	return resp;
};
